import React, { useCallback, useMemo, useState } from 'react';
import { ProductLite } from '../useMarketplaceState';

interface AddProductsDrawerProps {
  open: boolean;
  products: ProductLite[];
  /** IDs of products already in the target catalogue - these will be filtered out */
  existingProductIds?: Set<string>;
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
}

export function AddProductsDrawer(props: AddProductsDrawerProps) {
  const { open, products, existingProductIds, onClose, onConfirm, getCachedImageUrl } = props;
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState<'all' | 'software' | 'computers' | 'networking' | 'mobile-hardware' | 'consulting' | 'telstra' | 'unified-collaboration'>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const getDomainLabel = (domain?: string, kind?: string) => {
    switch (domain || kind) {
      case 'mobile-hardware':
        return 'Telstra Handset';
      case 'telstra':
        return 'Telstra Plan';
      case 'unified-collaboration':
        return 'Unified Collab';
      case 'software':
        return 'Software';
      case 'consulting':
        return 'Consulting';
      case 'networking':
        return 'Networking';
      case 'computers':
        return 'Computers';
      default:
        return domain || kind || 'Product';
    }
  };

  const matchesDomainFilter = useCallback((productDomain: string) => {
    if (domainFilter === 'all') return true;
    if (domainFilter === 'telstra') {
      return productDomain === 'telstra' || productDomain === 'mobile-hardware';
    }
    return productDomain === domainFilter;
  }, [domainFilter]);

  // Get unique brands from products
  const brands = useMemo(() => {
    const brandSet = new Set<string>();
    products.forEach((p) => {
      if (p.vendorOrBrand) {
        brandSet.add(p.vendorOrBrand);
      }
    });
    return Array.from(brandSet).sort();
  }, [products]);

  // Filter products based on search, domain, brand, and exclude products already in catalogue
  const filteredProducts = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    return products.filter((p) => {
      // Exclude products already in the target catalogue
      if (existingProductIds?.has(p.id)) {
        return false;
      }
      
      // Search filter
      const matchesSearch = !term || 
        p.name.toLowerCase().includes(term) || 
        (p.vendorOrBrand || '').toLowerCase().includes(term);
      
      // Domain filter - match against product domain (or infer from kind if domain not set)
      const productDomain = p.domain || (p.kind === 'software' ? 'software' : p.kind === 'consulting' ? 'consulting' : 'computers');
      const matchesDomain = matchesDomainFilter(productDomain);
      
      // Brand filter
      const matchesBrand = brandFilter === 'all' || p.vendorOrBrand === brandFilter;
      
      return matchesSearch && matchesDomain && matchesBrand;
    });
  }, [products, searchQuery, matchesDomainFilter, brandFilter, existingProductIds]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const handleSelectAll = () => {
    const allSelected = filteredProducts.every((p) => selected[p.id]);
    if (allSelected) {
      // Deselect all filtered
      const newSelected = { ...selected };
      filteredProducts.forEach((p) => {
        delete newSelected[p.id];
      });
      setSelected(newSelected);
    } else {
      // Select all filtered
      const newSelected = { ...selected };
      filteredProducts.forEach((p) => {
        newSelected[p.id] = true;
      });
      setSelected(newSelected);
    }
  };

  const handleConfirm = () => {
    const ids = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => k);
    onConfirm(ids);
    setSelected({});
    setSearchQuery('');
    setDomainFilter('all');
    setBrandFilter('all');
  };

  const handleClose = () => {
    setSelected({});
    setSearchQuery('');
    setDomainFilter('all');
    setBrandFilter('all');
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-[800px] max-w-[90vw] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Products to Catalogue</h2>
            <p className="text-sm text-gray-500">Select products to add to this catalogue</p>
          </div>
          <button 
            onClick={handleClose} 
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>

            {/* Domain Filter */}
            <select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Filter by domain"
            >
              <option value="all">All Domains</option>
              <option value="software">Software</option>
              <option value="computers">Computers</option>
              <option value="networking">Networking</option>
              <option value="consulting">Consulting</option>
              <option value="telstra">Telstra</option>
              <option value="unified-collaboration">Unified Collaboration</option>
            </select>

            {/* Brand Filter */}
            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-[200px]"
              aria-label="Filter by brand"
            >
              <option value="all">All Brands</option>
              {brands.map((brand) => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>

            {/* Select All Button */}
            <button
              onClick={handleSelectAll}
              className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            >
              {filteredProducts.every((p) => selected[p.id]) && filteredProducts.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>
        </div>

        {/* Product List */}
        <div className="flex-1 overflow-auto p-4">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
              </svg>
              <p className="text-lg font-medium mb-2">No products found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filteredProducts.map((p) => (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    selected[p.id]
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[p.id]}
                    onChange={(e) => setSelected((prev) => ({ ...prev, [p.id]: e.target.checked }))}
                    className="sr-only"
                  />
                  
                  {/* Product Image */}
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {p.image && getCachedImageUrl ? (
                      <img 
                        src={getCachedImageUrl(p.image)} 
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21,15 16,10 5,21" />
                      </svg>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="truncate">{p.vendorOrBrand || 'Unknown'}</span>
                      <span className="text-gray-300">•</span>
                      <span className={
                        p.domain === 'software' ? 'text-blue-600' :
                        p.domain === 'consulting' ? 'text-purple-600' :
                        p.domain === 'telstra' || p.domain === 'mobile-hardware' ? 'text-orange-600' :
                        p.domain === 'unified-collaboration' ? 'text-indigo-600' :
                        'text-green-600'
                      }>{getDomainLabel(p.domain, p.kind)}</span>
                    </div>
                  </div>

                  {/* Checkbox Visual */}
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    selected[p.id]
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-gray-300'
                  }`}>
                    {selected[p.id] && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-xl flex-shrink-0">
          <div className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">{selectedCount}</span> product{selectedCount !== 1 ? 's' : ''} selected
            {filteredProducts.length !== products.length && (
              <span className="text-gray-400 ml-2">
                (showing {filteredProducts.length} of {products.length})
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCount === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Add {selectedCount > 0 ? `(${selectedCount})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddProductsDrawer;
